
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PropBot } from '@/types/prop-bots';
import { Award, Timer, Percent, TrendingDown, DollarSign, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { Progress } from '@/components/ui/progress';
import { useEffect, useState } from 'react';
import { EnhancedPropBotStats as EnhancedPropBotStatsType, EnhancedPropBotStatsService } from '@/services/prop-bot/enhanced-prop-bot-stats-service';

interface EnhancedPropBotStatsProps {
  bot: PropBot;
  enhancedStats?: EnhancedPropBotStatsType;
}

export const EnhancedPropBotStats = ({ bot, enhancedStats }: EnhancedPropBotStatsProps) => {
  const { t } = useTranslation();
  
  // Robust extraction of propDetails - handle both array and object formats
  const propDetails = Array.isArray(bot.propDetails) && bot.propDetails.length > 0 
    ? bot.propDetails[0] 
    : bot.propDetails;
    
  const isMobile = useIsMobile();
  // Use enhanced stats if available, fallback to manual values
  const displayStats = enhancedStats || {
    winRate: parseFloat(propDetails?.winrate || '0'),
    maxDrawdown: parseFloat(propDetails?.max_drawdown || '0'),
    potentialProfit: parseFloat(propDetails?.potential_profit || '0'),
    passRate: parseFloat(propDetails?.pass_rate || '0'),
    estimatedPassTime: parseFloat(propDetails?.estimated_pass_time || '0'),
    connectedAccountsTotal: propDetails?.connected_accounts_total || 0,
    dataSource: {
      winRate: 'manual' as const,
      maxDrawdown: 'manual' as const,
      potentialProfit: 'manual' as const,
    }
  };

  const dataSourceLabels = enhancedStats ? 
    EnhancedPropBotStatsService.getDataSourceLabels(enhancedStats.dataSource) : 
    { winRate: '📝', maxDrawdown: '📝', potentialProfit: '📝' };

  const [animatedValues, setAnimatedValues] = useState({
    passRate: 0,
    winRate: 0,
    maxDrawdown: 0,
    potentialProfit: 0,
    connectedAccounts: 0,
  });

  // Animate numbers on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        passRate: displayStats.passRate,
        winRate: displayStats.winRate,
        maxDrawdown: displayStats.maxDrawdown,
        potentialProfit: displayStats.potentialProfit,
        connectedAccounts: displayStats.connectedAccountsTotal,
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [displayStats]);

  const stats = [
    {
      label: t('pass_rate'),
      value: `${animatedValues.passRate}%`,
      originalValue: displayStats.passRate,
      icon: Award,
      highlight: true,
      color: 'primary',
      progress: animatedValues.passRate,
      maxProgress: 100,
      dataSource: '📝', // Always manual for pass rate
    },
    {
      label: t('est_pass_time'),
      value: `${displayStats.estimatedPassTime} ${t('days')}`,
      originalValue: displayStats.estimatedPassTime,
      icon: Timer,
      highlight: true,
      color: 'primary',
      progress: Math.min(displayStats.estimatedPassTime * 2, 100),
      maxProgress: 100,
      dataSource: '📝', // Always manual for estimated pass time
    },
    {
      label: t('win_rate'),
      value: `${animatedValues.winRate}%`,
      originalValue: displayStats.winRate,
      icon: Percent,
      color: 'blue',
      progress: animatedValues.winRate,
      maxProgress: 100,
      dataSource: dataSourceLabels.winRate,
    },
    {
      label: t('max_drawdown'),
      value: `${animatedValues.maxDrawdown}%`,
      originalValue: displayStats.maxDrawdown,
      icon: TrendingDown,
      color: 'red',
      progress: animatedValues.maxDrawdown,
      maxProgress: 100,
      dataSource: dataSourceLabels.maxDrawdown,
    },
    {
      label: dataSourceLabels.potentialProfit === '📈' ? t('total_profit') : t('potential_profit'),
      value: `${animatedValues.potentialProfit}%`,
      originalValue: displayStats.potentialProfit,
      icon: DollarSign,
      color: 'green',
      progress: animatedValues.potentialProfit,
      maxProgress: 100,
      dataSource: dataSourceLabels.potentialProfit,
    },
    {
      label: t('active_accounts'),
      value: animatedValues.connectedAccounts.toString(),
      originalValue: displayStats.connectedAccountsTotal,
      icon: Users,
      color: 'purple',
      progress: Math.min(animatedValues.connectedAccounts * 5, 100),
      maxProgress: 100,
      dataSource: '📝', // Always manual for connected accounts
    },
  ];

  const getColorClasses = (color: string, highlight?: boolean) => {
    const colors = {
      primary: {
        bg: highlight ? 'bg-primary/15' : 'bg-primary/10',
        border: highlight ? 'border-primary/30' : 'border-primary/20',
        text: 'text-primary',
        icon: 'text-primary',
        progress: 'bg-primary',
        shadow: 'shadow-primary/20',
      },
      blue: {
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/20',
        text: 'text-blue-500',
        icon: 'text-blue-500',
        progress: 'bg-blue-500',
        shadow: 'shadow-blue-500/20',
      },
      green: {
        bg: 'bg-green-500/10',
        border: 'border-green-500/20',
        text: 'text-green-500',
        icon: 'text-green-500',
        progress: 'bg-green-500',
        shadow: 'shadow-green-500/20',
      },
      red: {
        bg: 'bg-red-500/10',
        border: 'border-red-500/20',
        text: 'text-red-500',
        icon: 'text-red-500',
        progress: 'bg-red-500',
        shadow: 'shadow-red-500/20',
      },
      purple: {
        bg: 'bg-purple-500/10',
        border: 'border-purple-500/20',
        text: 'text-purple-500',
        icon: 'text-purple-500',
        progress: 'bg-purple-500',
        shadow: 'shadow-purple-500/20',
      },
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <Card data-tour="prop-bot-stats" className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-muted/30 via-background to-muted/10" />
      
      <CardHeader className={`${isMobile ? "p-4" : "p-6"} pb-4 relative`}>
        <CardTitle className={`${isMobile ? "text-lg" : "text-xl"} flex items-center gap-2`}>
          <div className="w-1 h-6 bg-gradient-to-b from-primary to-primary/50 rounded-full" />
          {t('key_statistics')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} relative`}>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.map((stat, index) => {
            const colorClasses = getColorClasses(stat.color, stat.highlight);
            
            return (
              <div 
                key={stat.label}
                className={`group relative overflow-hidden rounded-xl border transition-all duration-500 hover:scale-105 hover:shadow-lg cursor-pointer ${
                  colorClasses.bg
                } ${colorClasses.border} ${colorClasses.shadow}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Hover effect overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className={`relative ${isMobile ? "p-3" : "p-4"}`}>
                  {/* Icon and label */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`${isMobile ? "p-1.5" : "p-2"} rounded-lg ${colorClasses.bg} transition-transform duration-300 group-hover:scale-110`}>
                      <stat.icon className={`${isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} ${colorClasses.icon}`} />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`${isMobile ? "text-xs" : "text-sm"} font-medium ${colorClasses.text} uppercase tracking-wider`}>
                        {stat.label}
                      </span>
                      <span className="text-xs opacity-60" title={stat.dataSource === '📈' ? 'Growth Chart Data' : 'Manual Data'}>
                        {stat.dataSource}
                      </span>
                    </div>
                  </div>
                  
                  {/* Value */}
                  <div className={`${isMobile ? "text-lg" : "text-2xl"} font-bold mb-2 transition-all duration-300 ${
                    stat.highlight ? colorClasses.text : 'text-foreground'
                  }`}>
                    {stat.value}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="space-y-1">
                    <Progress 
                      value={stat.progress} 
                      className={`h-1.5 ${colorClasses.bg}`}
                      style={{
                        transition: 'all 1s ease-out',
                        transitionDelay: `${index * 150}ms`,
                      }}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0</span>
                      <span>{stat.maxProgress === 100 ? '100%' : stat.maxProgress}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
